import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import {
  clearFilter,
  setFilterData,
  setFilterStatus,
  updateFilterData,
  clearSourceFilterData,
  setFilterAppliedStatus
} from '#action/list-filter';
import {
  Search,
  DatePicker,
  FilterButton,
  MultipleSelect,
  RadioButtonsGroup,
  MultipleCheckboxes
} from './components/general';
import {
  MultipleSelect as MobileMultipleSelect,
  MobileRadioButtons
} from './components/mobile';
import {
  ProjectFilterSearch,
  SubprojectRadioButtonsGroup,
  MobileSubprojectRadioButtonsGroup
} from './components/project';
import { useApplyFilterToUrl } from './hooks/useUpdateURLParams';
import {
  useSyncFilter,
  useFilterPristine,
  useUpdateInitialState,
  useUpdateLocalStorage
} from './hooks';
import Filter from './filter';
import FilterMobile from './filter-mobile';
import { Wrapper } from './filter.styles';
import { updateURLParameter, getURLParameter } from '#helper';

const defaultComponents = {
  general: {
    MultipleSelect,
    RadioButtonsGroup,
    MultipleCheckboxes,
    SubprojectRadioButtonsGroup
  },
  mobile: {
    MultipleSelect: MobileMultipleSelect,
    RadioButtonsGroup: MobileRadioButtons,
    MultipleCheckboxes,
    SubprojectRadioButtonsGroup: MobileSubprojectRadioButtonsGroup
  }
};

const defaultSearchComponents = {
  Search,
  FilterButton,
  ProjectFilterSearch
};

const defaultActions = {
  clearFilter,
  setFilterData,
  setFilterStatus,
  updateFilterData,
  clearSourceFilterData
};

const FilterWrapper = ({ data = {} }) => {
  const { customActions, searchProps, dateRangeComponent, disableSync } = data;
  const theme = useTheme();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const handleUpdateInitState = useUpdateInitialState();
  const applyFilterToURL = useApplyFilterToUrl();
  const applyFilterToLS = useUpdateLocalStorage(disableSync);

  const actions = useMemo(() => ({
    ...defaultActions,
    ...customActions
  }), [customActions]);

  const resetPagination = useCallback(() => {
    if (actions.changePage) {
      dispatch(actions.changePage(1, { refetch: false }));
    }
    const currentPage = +getURLParameter('page');
    if (currentPage !== 1 && currentPage !== null) {
      updateURLParameter('page', 1);
    }
  }, [actions, dispatch]);

  const handleChange = useCallback((value, name, id) => {
    dispatch(setFilterAppliedStatus(false));
    dispatch(actions.updateFilterData(value, name, id));
  }, [actions, dispatch]);

  const handleFetchFilterData = useCallback(() => {
    applyFilterToURL();
    applyFilterToLS();

    resetPagination();

    dispatch(actions.fetchFilterData());
    handleUpdateInitState();
    dispatch(setFilterAppliedStatus(true));
  }, [
    actions,
    dispatch, handleUpdateInitState,
    resetPagination, applyFilterToURL, applyFilterToLS
  ]);

  useSyncFilter(actions, disableSync);
  useFilterPristine();

  const getComponents = useCallback(() => {
    const components = data.components || [];
    const rendered = components.map(componentData => {
      const { type, ...props } = componentData;
      const view = isMobile ? 'mobile' : 'general';
      const Component = defaultComponents[view][type];

      if (Component) {
        const { id, name, relatedTo } = props;
        return <Component
          key={id}
          onChange={value => handleChange(value, name, relatedTo)}
          {...props}
        />;
      }
      return null;
    });

    return rendered;
  }, [data.components, handleChange, isMobile]);

  const SearchComponent = defaultSearchComponents[searchProps.type];
  const renderedComponents = useMemo(() => getComponents(), [getComponents]);
  const showFilterButton = Boolean(renderedComponents.length);

  return (
    <>
      <Wrapper>
        <SearchComponent
          open={open}
          setModalOpen={setOpen}
          dispatch={dispatch}
          updateFilterData={actions.updateFilterData}
          setFilterData={actions.setFilterData}
          fetchFilterData={actions.fetchFilterData}
          resetPagination={resetPagination}
          handleUpdateInitState={handleUpdateInitState}
          setFilterAppliedStatus={setFilterAppliedStatus}
          showFilterButton={showFilterButton}
          {...searchProps}
        />
        {open && !isMobile &&
          <Filter
            open={open}
            setModalOpen={setOpen}
            handleFetchFilterData={handleFetchFilterData}
          >
            {renderedComponents}
          </Filter>}
        {open && isMobile &&
          <FilterMobile
            open={open}
            setModalOpen={setOpen}
            handleFetchFilterData={handleFetchFilterData}
          >
            {renderedComponents}
          </FilterMobile>}
      </Wrapper>
      {dateRangeComponent &&
        <DatePicker
          handleChange={handleChange}
          handleFetchFilterData={handleFetchFilterData}
        />}
    </>
  );
};

export default FilterWrapper;
